import { Suspense } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { JSONContent } from '@tiptap/react';
import { dropLeadingHeading, type ArticleDetailDto } from '@zoxo/shared';

import { ErrorBoundary } from '../components/error-boundary';
import { PageContainer } from '../components/layout/page-container';
import { PageHeader } from '../components/ui/page-header';
import { GatePrompt, useActionGate } from '../features/auth/action-gate';
import { useMe } from '../features/auth/use-me';
import {
  ArticleEditor,
  type ArticleEditorInitial,
} from '../features/articles/editor/article-editor';
import { useArticle } from '../features/articles/use-article';

/**
 * Edit-article route (V4 §C). CSR. Login + onboarding gated; the magazine editor
 * pre-fills from the article detail (Suspense read in an Error Boundary so a 404
 * shows a fallback). Ownership is enforced by the server (403). Sits under the
 * `pd.$id` Outlet layout.
 */
export const Route = createFileRoute('/articles/$id/edit')({
  // 로그인 화면 — 세션이 브라우저에만 있다. 서버는 레이아웃만 그린다(docs/SSR.md).
  ssr: false,
  component: EditArticlePage,
});

function EditArticlePage() {
  const { id } = Route.useParams();
  const gate = useActionGate();
  const { data: me } = useMe();
  // 관리자는 온보딩(회사명) 없이도 남의 글을 고친다 — 콘솔과 같은 규칙. 권한은 서버가 본다.
  const allowed = gate === 'ready' || (gate === 'onboarding' && me?.role === 'admin');

  if (!allowed) {
    return (
      <PageContainer>
        <PageHeader title="기사 수정" />
        <GatePrompt status={gate as Exclude<typeof gate, 'ready'>} message="기사를 수정하려면 로그인이 필요해요." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ErrorBoundary resetKey={id}>
        <Suspense
          fallback={<p className="text-sm text-text-muted">불러오는 중…</p>}
        >
          <EditLoader id={id} />
        </Suspense>
      </ErrorBoundary>
    </PageContainer>
  );
}

function EditLoader({ id }: { id: string }) {
  const { data: article } = useArticle(id);
  return <ArticleEditor articleId={id} initial={toInitial(article)} />;
}

function toInitial(article: ArticleDetailDto): ArticleEditorInitial {
  return {
    title: article.title,
    // 상세와 같은 규칙: 본문 첫 h1 은 제목의 복제라 편집기에도 안 싣는다. 다음 저장에서 사라진다.
    body: isTiptapDoc(article.body) ? (dropLeadingHeading(article.body) as JSONContent) : undefined,
    tags: article.tags ?? [],
    status: article.status,
  };
}

function isTiptapDoc(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'doc'
  );
}
